/**
 * Verkauf von Geräten.
 *
 * Bewusst ein eigener Baustein statt eines Knopfes je Ansicht: Der Rückbau
 * betrifft Server, Router, Switches, Firewalls, Loadbalancer und USV
 * gleichermaßen, und in allen Fällen gilt dasselbe – vorher zeigen, was er einbringt und was er
 * löst, danach erst ausführen.
 *
 * Der Erlös steht dabei nicht als nackte Zahl da: Bei einem Server macht es
 * einen erheblichen Unterschied, ob die verbauten Bauteile mitgehen oder ins
 * eigene Lager wandern. Genau diese Abwägung soll der Dialog sichtbar machen.
 */
import { useMemo, useState } from 'react';
import type { DeviceKindKey } from '@hosting-sim/core';
import { findPart, quoteDecommission } from '@hosting-sim/core';
import { Button, Field, Modal, Notice } from './ui';
import { useGame } from '../store/game';
import { money } from '../lib/format';

const KIND_LABEL: Record<DeviceKindKey, string> = {
  server: 'Server',
  router: 'Router',
  switch: 'Switch',
  firewall: 'Firewall',
  loadbalancer: 'Loadbalancer',
  ups: 'USV',
};

/**
 * Knopf mit Bestätigungsdialog.
 *
 * Ist der Verkauf gerade nicht möglich (Kunden liegen noch darauf, Strecken
 * hängen daran), bleibt der Knopf sichtbar und nennt den Grund – das ist
 * hilfreicher als ein Knopf, der einfach verschwindet.
 */
export function SellDeviceButton({
  kind,
  id,
  name,
  size = 'sm',
  onSold,
}: {
  kind: DeviceKindKey;
  id: string;
  name: string;
  size?: 'sm' | 'md';
  onSold?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size={size} variant="danger" onClick={() => setOpen(true)}>
        Verkaufen
      </Button>
      {open && (
        <SellDeviceDialog
          kind={kind}
          id={id}
          name={name}
          onClose={() => setOpen(false)}
          onSold={() => {
            setOpen(false);
            onSold?.();
          }}
        />
      )}
    </>
  );
}

function SellDeviceDialog({
  kind,
  id,
  name,
  onClose,
  onSold,
}: {
  kind: DeviceKindKey;
  id: string;
  name: string;
  onClose: () => void;
  onSold: () => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [harvest, setHarvest] = useState(true);
  const [busy, setBusy] = useState(false);

  const quote = useMemo(() => (world ? quoteDecommission(world, kind, id) : null), [world, kind, id]);
  if (!quote) return null;

  const total = quote.proceeds + (harvest ? 0 : quote.partsProceeds);
  const partCount = quote.harvestable.reduce((sum, h) => sum + h.quantity, 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${KIND_LABEL[kind]} ${name} verkaufen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={!quote.ok}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await run({ type: 'hw/sell', kind, id, harvestParts: harvest });
                if (result.ok) onSold();
              } finally {
                setBusy(false);
              }
            }}
          >
            Verbindlich verkaufen
          </Button>
        </>
      }
    >
      {!quote.ok ? (
        <Notice tone="warning" title="Verkauf derzeit nicht möglich">
          {quote.error}
        </Notice>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Erlös für das Gerät">{money(quote.proceeds)}</Field>
            {quote.partsProceeds > 0 && <Field label="Wert der Bauteile">{money(quote.partsProceeds)}</Field>}
          </div>

          {quote.harvestable.length > 0 && (
            <div className="rounded-lg border divider p-3">
              <p className="mb-2 text-xs font-medium">Was mit den {partCount} verbauten Bauteilen geschehen soll</p>
              <label className="flex items-start gap-2 text-xs">
                <input type="radio" checked={harvest} onChange={() => setHarvest(true)} className="mt-0.5" />
                <span>
                  <strong>Ins Ersatzteillager übernehmen.</strong> Sie liegen danach am Standort bereit und lassen sich in
                  andere Server einbauen. Der Verkauf bringt dann nur den Restwert des Gehäuses.
                </span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-xs">
                <input type="radio" checked={!harvest} onChange={() => setHarvest(false)} className="mt-0.5" />
                <span>
                  <strong>Mitverkaufen.</strong> Bringt {money(quote.partsProceeds)} mehr, aber die Bauteile sind weg.
                </span>
              </label>

              <ul className="mt-2 space-y-0.5 border-t divider pt-2 text-2xs text-panel-500 dark:text-panel-400">
                {quote.harvestable.map((entry) => (
                  <li key={entry.modelKey}>
                    {entry.quantity}× {findPart(entry.modelKey)?.name ?? entry.modelKey}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {quote.sideEffects.length > 0 && (
            <Notice tone="info" title="Was der Rückbau außerdem löst">
              <ul className="ml-4 list-disc">
                {quote.sideEffects.map((effect) => (
                  <li key={effect}>{effect}</li>
                ))}
              </ul>
            </Notice>
          )}

          <Notice tone="warning" title={`Gesamterlös ${money(total)}`}>
            Der Vorgang lässt sich nicht rückgängig machen. Hardware verliert schnell an Wert – wer zu lange wartet, bekommt
            deutlich weniger.
          </Notice>
        </div>
      )}
    </Modal>
  );
}

/**
 * Hinweis auf Geräte, deren Weiterbetrieb sich nicht mehr lohnt.
 *
 * Der Punkt, an dem Wartung und Ausfallrisiko den Restwert übersteigen, ist von
 * außen schwer zu sehen – hier steht er.
 */
export function ReplacementHint({ onSelect }: { onSelect?: (serverId: string) => void }) {
  const world = useGame((s) => s.world);
  const candidates = useMemo(() => {
    if (!world) return [];
    // Direkt hier gerechnet statt importiert: Die Liste ist kurz und die
    // Bedingung soll neben der Anzeige stehen, nicht drei Dateien entfernt.
    return Object.values(world.servers)
      .filter((s) => s.health < 0.35)
      .slice(0, 5);
  }, [world]);

  if (candidates.length === 0) return null;

  return (
    <Notice tone="warning" title={`${candidates.length} Server in schlechtem Zustand`}>
      <p className="text-xs">
        Ab hier häufen sich Ausfälle, und der Restwert sinkt weiter. Rechtzeitig abgeben bringt oft mehr, als bis zum Defekt zu
        warten.
      </p>
      <ul className="mt-1 space-y-0.5 text-2xs">
        {candidates.map((server) => (
          <li key={server.id}>
            <button type="button" className="underline" onClick={() => onSelect?.(server.id)}>
              {server.name}
            </button>{' '}
            — Zustand {(server.health * 100).toFixed(0)} %
          </li>
        ))}
      </ul>
    </Notice>
  );
}
